<template>
  <v-card>
    <v-toolbar flat dark color="primary" dense>
      <v-toolbar-title class="font-weight-light">
        {{ incident.vehicleLabel }} &mdash; {{ formatTimestamp(incident.timestamp) }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <v-card-text class="pa-3">
      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <div class="mt-2 caption">{{ tv('Loading video') }}</div>
      </div>
      <div v-else-if="statusMessage" class="text-center py-8">
        <v-icon large :color="statusIconColor">{{ statusIcon }}</v-icon>
        <div class="mt-2 caption">{{ statusMessage }}</div>
        <v-btn v-if="canRetry" small outlined color="primary" class="mt-3" @click="fetchVideo">
          {{ tv('Retry') }}
        </v-btn>
      </div>
      <div v-else-if="videoUrl" style="width: 100%;">
        <video
          v-if="videoFormat !== 'flv'"
          :src="videoUrl"
          controls
          preload="auto"
          style="width: 100%; max-height: 400px; background: #000;"
          :key="videoUrl"
          @error="handleVideoError"
        ></video>
        <video
          v-else
          ref="flvVideo"
          controls
          playsinline
          style="width: 100%; max-height: 400px; background: #000;"
        ></video>
        <div v-if="videoFormat !== 'flv'" class="text-right mt-1">
          <a :href="videoUrl" target="_blank" rel="noopener" class="caption grey--text">
            <v-icon x-small class="grey--text">mdi-open-in-new</v-icon> {{ tv('Open in new tab') }}
          </a>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import httpClient from '@/api/httpClient'
import { createFlvPlayer, destroyFlvPlayer, isFlvSupported } from '@/utils/flvPlayer'
import formatDate from '@/utils/formatDateHelper.js'
import translate from '@/utils/genTable/multilanguageHelper.js'

export default {
  name: 'Camera360VideoPlayerDialog',
  props: {
    incident: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      videoUrl: null,
      videoFormat: null,
      flv: null,
      loading: false,
      statusMessage: null,
      canRetry: false
    }
  },
  computed: {
    statusIcon() {
      return this.canRetry ? 'mdi-clock-outline' : 'mdi-video-off-outline'
    },
    statusIconColor() {
      return this.canRetry ? 'orange' : 'grey'
    }
  },
  mounted() {
    this.fetchVideo()
  },
  beforeDestroy() {
    this.stopFlv()
  },
  methods: {
    tv(key) {
      return translate(key, 'pages.video360')
    },
    formatTimestamp(ts) {
      if (!ts) return '-'
      return formatDate(ts, 'medium')
    },
    async fetchVideo() {
      this.loading = true
      this.statusMessage = null
      this.canRetry = false
      this.stopFlv()
      this.videoUrl = null
      this.videoFormat = null

      try {
        // alarmId is a string (19-digit id for Streamax) -- never passed through Number
        const response = await httpClient.get(
          `camera360/vehicles/${this.incident.vehicleId}/alarms/${
            this.incident.alarmId
          }/video`
        )
        const { downloadStatus, url, format } = response.data

        if (downloadStatus === 'COMPLETED' && url) {
          this.videoFormat = format
          this.videoUrl = url
          if (format === 'flv') {
            this.loading = false
            await this.$nextTick()
            this.playFlv()
          }
        } else if (
          downloadStatus === 'WAITING' ||
          downloadStatus === 'DOWNLOADING'
        ) {
          this.statusMessage = this.tv(
            'Video still processing, try again shortly'
          )
          this.canRetry = true
        } else if (downloadStatus === 'NOTCONFIGURED' || !downloadStatus) {
          this.statusMessage = this.tv(
            'No video configured for this incident type'
          )
        } else {
          this.statusMessage = this.tv('Video not available')
        }
      } catch (e) {
        this.statusMessage = this.tv('Failed to load video')
      } finally {
        this.loading = false
      }
    },
    playFlv() {
      if (!isFlvSupported()) {
        this.showError(this.tv('Source not supported'))
        return
      }

      this.flv = createFlvPlayer(this.$refs.flvVideo, this.videoUrl, (type, detail) => {
        console.error('Camera360 recording error', type, detail)
        this.showError(this.tv('Video failed to load'))
      })
    },
    stopFlv() {
      destroyFlvPlayer(this.flv)
      this.flv = null
    },
    showError(message) {
      this.stopFlv()
      this.statusMessage = message
      this.videoUrl = null
    },
    handleVideoError(e) {
      const video = e.target
      const err = video.error
      const messages = {
        1: this.tv('Playback aborted'),
        2: this.tv('Network error'),
        3: this.tv('Decode error'),
        4: this.tv('Source not supported')
      }
      this.statusMessage = err
        ? messages[err.code] || `Video error code ${err.code}`
        : this.tv('Video failed to load')
      this.videoUrl = null
    }
  }
}
</script>
